import React, { useLayoutEffect, useRef } from "react";
import { useTransitionPresence } from "../hooks/useTransitionPresence.js";
import { MOTION_DURATION_MS } from "../motion.js";
import { useDisclosureContext } from "./disclosure-context.js";

/* DisclosurePanel — the body that collapses (ADR-0091).

   It opens on a 0fr → 1fr grid row, so it animates to its own height with
   nobody measuring anything, and collapses the same way on close. It adds no
   padding of its own. This is the ONE place a closed disclosure is hidden from
   the a11y tree and the tab order, for Disclosure and the split parts alike. */

export interface DisclosurePanelProps {
  /** Classes for the panel's inner content wrapper. */
  className?: string | undefined;
  /** Drop the children once the collapse has finished, and mount them again
   *  on open. Default `false`: the children stay mounted (ADR-0091). */
  unmountOnClose?: boolean | undefined;
  children: React.ReactNode;
}

export function DisclosurePanel({
  className,
  unmountOnClose = false,
  children,
}: DisclosurePanelProps) {
  const { open, panelId } = useDisclosureContext("DisclosurePanel");
  const panelRef = useRef<HTMLDivElement | null>(null);
  const expanded = open ? "true" : "false";

  // Dropping the children on the frame the flag flips would cut the collapse
  // off, so they stay for the grid row's transition — the clock overlays
  // retain their exits on. Reduced motion has no collapse to wait for.
  const { present } = useTransitionPresence(open, MOTION_DURATION_MS.state);
  const dropChildren = unmountOnClose && !present;

  // React 18 serialises `inert={false}` as the string "false", which HTML reads
  // as inert, so the attribute can only be toggled imperatively.
  useLayoutEffect(() => {
    panelRef.current?.toggleAttribute("inert", !open);
  }, [open]);

  return (
    /* The panel stays mounted so the collapse can run; `hidden` would kill
       the transition, so visibility is the grid row's job and the content is
       taken out of the tab order and the a11y tree instead. */
    <div id={panelId} data-expanded={expanded} className="pb-disclosure">
      <div
        ref={panelRef}
        className="pb-disclosure-panel"
        {...(open ? {} : { "aria-hidden": "true" as const })}
      >
        <div className={className}>{dropChildren ? null : children}</div>
      </div>
    </div>
  );
}
